import { Link } from "react-router-dom";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import Button from "@/components/base/Button";
import { useCompanyInfo } from "@/hooks/usePublicContent";

const serviceLinks = [
  { label: "Mudanzas", to: "/servicios" },
  { label: "Acarreo", to: "/servicios" },
  { label: "Transporte para eventos", to: "/servicios" },
  { label: "Distribución", to: "/servicios" },
  { label: "Servicios especiales", to: "/servicios" },
];

const companyLinks = [
  { label: "Nosotros", to: "/nosotros" },
  { label: "Nuestra flota", to: "/flota" },
  { label: "Trabajos realizados", to: "/trabajos" },
  { label: "Contacto", to: "/contacto" },
  { label: "Solicitar servicio", to: "/solicitar-servicio" },
];

export default function Footer() {
  const companyInfo = useCompanyInfo();

  return (
    <footer className="bg-primary-950 text-primary-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="mb-12 flex flex-col items-start gap-5 rounded-lg border border-primary-50/15 bg-primary-900/60 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/15 px-3 py-1 text-xs font-label font-semibold uppercase tracking-[0.18em] text-accent-300">
              <i className="ri-steering-2-line text-sm leading-none" aria-hidden="true"></i>
              Trabaja con nosotros
            </span>
            <h3 className="mt-3 text-lg font-heading font-bold text-primary-50 md:text-xl">
              ¿Tienes un vehículo o una empresa de transporte?
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-primary-200">
              Forma parte de la red de colaboradores de {companyInfo.nombre} y pon tus vehículos a
              disposición para nuevas oportunidades de servicio.
            </p>
          </div>
          <Button
            to="/trabaja-con-nosotros"
            variant="accent"
            size="lg"
            icon="ri-user-add-line"
            className="shrink-0"
          >
            Quiero formar parte
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo size="lg" boxed />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-primary-200">
              {companyInfo.descripcionCorta} Unidades modernas, equipo profesional y coordinación
              personalizada para cada operación.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {companyInfo.redes.map((red) => (
                <a
                  key={red.nombre}
                  href={red.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={red.nombre}
                  className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50/10 text-primary-100 transition-colors duration-200 hover:bg-accent-500 hover:text-primary-950"
                >
                  <i className={`${red.icono} text-base leading-none`} aria-hidden="true"></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-heading font-semibold uppercase tracking-wider text-primary-50">
              Servicios
            </h3>
            <ul className="mt-4 space-y-2.5">
              {serviceLinks.map((link, index) => (
                <li key={`${link.label}-${index}`}>
                  <Link
                    to={link.to}
                    className="text-sm text-primary-200 transition-colors duration-200 hover:text-accent-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-heading font-semibold uppercase tracking-wider text-primary-50">
              Empresa
            </h3>
            <ul className="mt-4 space-y-2.5">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="text-sm text-primary-200 transition-colors duration-200 hover:text-accent-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-heading font-semibold uppercase tracking-wider text-primary-50">
              Contacto
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-primary-200">
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-accent-300">
                  <i className="ri-map-pin-2-line text-base leading-none" aria-hidden="true"></i>
                </span>
                <span>
                  {companyInfo.direccion}
                  <br />
                  {companyInfo.ciudad}
                </span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-accent-300">
                  <i className="ri-phone-line text-base leading-none" aria-hidden="true"></i>
                </span>
                <a href={companyInfo.telefonoLink} className="transition-colors hover:text-accent-300">
                  {companyInfo.telefono}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-accent-300">
                  <i className="ri-mail-line text-base leading-none" aria-hidden="true"></i>
                </span>
                <a href={companyInfo.emailLink} className="transition-colors hover:text-accent-300">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 items-center justify-center text-accent-300">
                  <i className="ri-time-line text-base leading-none" aria-hidden="true"></i>
                </span>
                <span>{companyInfo.horario}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-primary-50/15 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-primary-300">
            © {new Date().getFullYear()} {companyInfo.nombre}. Todos los derechos reservados.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <Link to="/contacto" className="text-xs text-primary-300 transition-colors hover:text-accent-300">
              Términos y condiciones
            </Link>
            <Link to="/contacto" className="text-xs text-primary-300 transition-colors hover:text-accent-300">
              Política de privacidad
            </Link>
            <Link to="/admin/login" className="text-xs text-primary-300 transition-colors hover:text-accent-300">
              Acceso administrador
            </Link>
            <ThemeToggle light />
          </div>
        </div>
      </div>
    </footer>
  );
}